/**
 * PlayerDetailRight View Component
 *
 * Pure view component for player detail right column.
 * Displays either notes or player recommendations based on type prop.
 * @remarks Uses helper functions from utils.ts to reduce cognitive complexity.
 * @module components/PlayerDetailRight/PlayerDetailRightView
 */

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";
import { BookX, NotebookPenIcon } from "lucide-react-native";
import React, { ReactElement, memo } from "react";
import { Platform } from "react-native";
import DraggableFlatList, { RenderItem } from "react-native-draggable-flatlist";

import { getHeadingClasses } from "@/utils/playerDetailRight";

import CategoryButton from "./CategoryButton";
import ItemSeparator from "@/components/molecules/ItemSeparator";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { FlatList } from "@/components/atoms/FlatList";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";

import type { ListRenderItem } from "@/components/atoms/FlashList";

import NotesForm from "@/features/notes/components/organisms/NotesForm";
import NotesList from "@/features/notes/components/organisms/NotesList";
import RecommendationPlayer from "@/features/recommendations/components/organisms/RecommendationPlayer";
import { RecommendationsPlayerRecommendationResultFragment } from "@/generated/graphql";
import { NoteActionsheetState } from "@/stores/noteActionsheetState";
import { IRecommendationsCategorySelection } from "@/stores/recommendationsCategorySelection";
import defaultExtractKey from "@/utils/defaultExtractKey";
import { ifWeb } from "@/utils/platform";

/**
 * Props for RightColumnView component
 */
interface IRightColumnProps {
  type: "note" | "recommendation";
  playerId?: string;
  recommendationsCategorySelected: string;
  handleSelectRecommendationsCategory: (type: string) => void;
  recommendationTypes: IRecommendationsCategorySelection[];
  data: RecommendationsPlayerRecommendationResultFragment[];
  refreshing: boolean;
  noteState: NoteActionsheetState;
  isSmallScreen: boolean;
  onChangeStatusNote: () => void;
  onRefresh: () => void;
  renderListEmptyComponent: () => ReactElement | null;
  isDark: boolean;
}

const renderRecommendationItem: ListRenderItem<
  RecommendationsPlayerRecommendationResultFragment
> = ({ item }) => <RecommendationPlayer data={item} />;

const renderDraggableRecommendationItem: RenderItem<
  RecommendationsPlayerRecommendationResultFragment
> = ({ item }) => <RecommendationPlayer data={item} />;

/**
 * Pure view component for player detail right column
 * Displays either notes or player recommendations based on type prop
 * @param root0 - Component props
 * @param root0.type - Column type: "note" or "recommendation"
 * @param root0.data - Recommendation data to display
 * @param root0.playerId - Player ID for notes
 * @param root0.refreshing - Whether content is refreshing
 * @param root0.noteState - Current state of notes UI
 * @param root0.onRefresh - Handler for pull to refresh
 * @param root0.recommendationTypes - Available recommendation categories
 * @param root0.renderListEmptyComponent - Function to render empty state
 * @param root0.recommendationsCategorySelected - Currently selected category
 * @param root0.isSmallScreen - Whether device has small screen
 * @param root0.handleSelectRecommendationsCategory - Handler for category selection
 * @param root0.onChangeStatusNote - Handler for toggling note form/list
 * @param root0.isDark - Whether dark theme is active
 */
const RightColumnView = ({
  type,
  data,
  playerId,
  refreshing,
  noteState,
  onRefresh,
  recommendationTypes,
  renderListEmptyComponent,
  recommendationsCategorySelected,
  isSmallScreen,
  handleSelectRecommendationsCategory,
  onChangeStatusNote,
  isDark,
}: IRightColumnProps) => (
  <Stack
    className={`w-1/4 ${!isSmallScreen ? "border-l border-surface-muted/50 pl-2 pr-4" : "border-l-4 border-surface-strong pl-4"}`}
  >
    {type === "recommendation" && (
      <>
        <Box
          className={`min-h-10 flex-row items-center border-b border-surface-muted/50 ${!isSmallScreen ? "p-4" : "px-2 pb-4"}`}
        >
          <Heading
            variant="title-lg"
            className={getHeadingClasses({ isSmallScreen, isDark })}
          >
            Featured Players
          </Heading>
        </Box>
        <ScrollView
          horizontal
          className="shrink-0 grow-0"
          contentContainerClassName={!isSmallScreen ? "gap-2 p-3" : "gap-4 p-4"}
          showsHorizontalScrollIndicator={ifWeb(true, false)}
        >
          {recommendationTypes.map(item => (
            <CategoryButton
              key={item.type}
              item={item}
              isSelected={recommendationsCategorySelected === item.type}
              isDark={isDark}
              isSmallScreen={isSmallScreen}
              onPress={handleSelectRecommendationsCategory}
            />
          ))}
        </ScrollView>
        <Box className="scrollbar-hide web:overflow-y-auto flex-1">
          {Platform.OS === "web" ? (
            <FlatList
              contentContainerClassName={`flex-grow ${!isSmallScreen ? "p-4" : "p-3"} pt-0`}
              data={data}
              refreshing={refreshing}
              keyExtractor={defaultExtractKey}
              scrollEventThrottle={16}
              ItemSeparatorComponent={ItemSeparator}
              onRefresh={onRefresh}
              renderItem={renderRecommendationItem}
              ListEmptyComponent={renderListEmptyComponent}
            />
          ) : (
            <DraggableFlatList
              contentContainerClassName={`flex-grow ${!isSmallScreen ? "p-4" : "p-3"} pt-0`}
              data={data}
              refreshing={refreshing}
              keyExtractor={defaultExtractKey}
              removeClippedSubviews
              decelerationRate="normal"
              scrollEventThrottle={16}
              ItemSeparatorComponent={ItemSeparator}
              onRefresh={onRefresh}
              renderItem={renderDraggableRecommendationItem}
              ListEmptyComponent={renderListEmptyComponent}
            />
          )}
        </Box>
      </>
    )}
    {type === "note" && (
      <Stack className="flex-1">
        <Stack
          direction="horizontal"
          className={`items-center justify-between border-b border-surface-muted/50 ${!isSmallScreen ? "p-4" : "px-4 pb-4"}`}
        >
          <Heading
            variant="title-lg"
            className={`flex-1 ${!isSmallScreen ? "text-title" : ""}`}
          >
            Notes
          </Heading>
          <Button
            testID={TEST_ID_PLAYER_DETAIL.ADD_NEW_NOTE_BUTTON}
            variant="link"
            icon={
              noteState === NoteActionsheetState.Form ? BookX : NotebookPenIcon
            }
            iconSize="md"
            onPress={onChangeStatusNote}
            hitSlop={{ top: 16, left: 16, right: 16, bottom: 16 }}
            accessible
            accessibilityLabel={
              noteState === NoteActionsheetState.Form
                ? "Cancel note creation"
                : "Create new note"
            }
            accessibilityRole="button"
          />
        </Stack>
        {noteState === NoteActionsheetState.Form ? (
          <NotesForm playerId={playerId ?? ""} />
        ) : (
          <NotesList playerId={playerId ?? ""} />
        )}
      </Stack>
    )}
  </Stack>
);

RightColumnView.displayName = "RightColumnView";

export default memo(RightColumnView);
